import {Button, DataStatus, EmptyStatusAction} from 'platform/components';
import {
  Box,
  Grid,
  Show,
  Space,
  useElementSize,
  useResponsivePropValue,
  ValueByDevice,
  VStack,
} from 'platform/foundation';
import styled from 'styled-components';

import {isNil} from 'ramda';

import {TaskResponseBody} from '@dms/api/core';
import {GetUserApiResponse} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {getVisibleRowCount, STRETCH_FACTOR} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {TaskCenterListItem} from './components/TaskCenterListItem/TaskCenterListItem';

interface TaskCenterListBaseProps extends RequiredTestIdProps {
  list: TaskResponseBody[];
  isLoading: boolean;
  isError: boolean;
  onItemClick: (task: TaskResponseBody) => void;
  naturalNumberOfTasks?: number | ValueByDevice<number>;
  maximumNumberOfTasks?: number | ValueByDevice<number>;
  columns?: number | ValueByDevice<number>;
  isFullHeight?: boolean;
  onAssigneeClick?: (assignee: GetUserApiResponse) => void;
  hideAssignee?: boolean;
  emptyStatus?: {
    message?: string;
    action?: EmptyStatusAction;
  };
}

type TaskCenterListItemProps = TaskCenterListBaseProps &
  (
    | {
        onShowAllClick?: () => void;
        shouldRenderShowAllButton?: false;
      }
    | {
        onShowAllClick: () => void;
        shouldRenderShowAllButton?: true;
      }
  );

const DEFAULT_MAX_TASKS_TO_RENDER = [4, 5, 6] as const satisfies ValueByDevice<number>;
const DEFAULT_TASK_COLUMNS = 1;

// Tasks vary in height, so a row is measured rather than assumed. Dividing the natural block by
// its row count also folds the gap between rows into the cost of adding one more row.
const getRowHeight = (naturalBlockHeight: number | null, naturalRowCount: number) =>
  isNil(naturalBlockHeight) || naturalRowCount === 0 ? null : naturalBlockHeight / naturalRowCount;

// `contain: size` keeps the tasks drawn here out of the height the row count is derived from.
const StyledStretchRegion = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  contain: size;
`;

export function TaskCenterList(props: TaskCenterListItemProps) {
  const [regionRef, , regionHeight] = useElementSize<HTMLDivElement>();
  // Measured on a wrapper that outlives the loading state: DataStatus renders a spinner instead of
  // the rows on a cold load, and an observer attached to a row that appears later never subscribes.
  const [naturalTasksRef, , naturalTasksHeight] = useElementSize<HTMLDivElement>();

  const naturalTaskCount = useResponsivePropValue(
    props.naturalNumberOfTasks ?? DEFAULT_MAX_TASKS_TO_RENDER
  );
  const columns = useResponsivePropValue(props.columns ?? DEFAULT_TASK_COLUMNS);
  const maximumTaskCount = useResponsivePropValue(
    props.maximumNumberOfTasks ?? naturalTaskCount * STRETCH_FACTOR
  );
  const naturalTaskRows = Math.ceil(naturalTaskCount / columns);
  const renderedNaturalTaskCount = Math.min(
    props.list?.length ?? 0,
    naturalTaskCount,
    maximumTaskCount
  );
  const renderedNaturalRows = Math.ceil(renderedNaturalTaskCount / columns);
  const visibleTaskRows = props.isFullHeight
    ? getVisibleRowCount({
        naturalRows: naturalTaskRows,
        regionHeight,
        rowHeight: getRowHeight(naturalTasksHeight, renderedNaturalRows),
      })
    : naturalTaskRows;
  const visibleTaskCount = Math.min(visibleTaskRows * columns, maximumTaskCount);

  const tasks = props.list?.slice(0, visibleTaskCount) ?? [];
  const naturalTasks = tasks.slice(0, naturalTaskCount);
  const stretchedTasks = tasks.slice(naturalTaskCount);

  return (
    <Box height="100%" borderRadius="medium" overflow="hidden">
      <VStack
        height="100%"
        justify={tasks.length === 0 ? 'center' : 'flex-start'}
        spacing={1}
        data-testid={suffixTestId('task-center-list', props)}
      >
        <Box ref={naturalTasksRef}>
          <DataStatus
            isLoading={props.isLoading}
            isError={props.isError}
            minHeight={80}
            isEmpty={tasks.length < 1}
            emptyMessage={props.emptyStatus?.message ?? i18n.t('entity.task.labels.emptyTasks')}
            action={
              props.emptyStatus?.action ?? {
                title: i18n.t('entity.task.actions.showAll'),
                onClick: props.onShowAllClick,
                rightIcon: 'navigation/chevron_right',
              }
            }
          >
            <Grid columns={columns} spacing={1} data-testid={suffixTestId('naturalTasks', props)}>
              {naturalTasks.map((task, index) => (
                <Box key={task.id}>
                  <TaskCenterListItem
                    data-testid={`task-center-listItem-${index}`}
                    task={task}
                    onAssigneeClick={props.onAssigneeClick}
                    onClick={() => props.onItemClick(task)}
                    hideAssignee={props.hideAssignee}
                  />
                </Box>
              ))}
            </Grid>
          </DataStatus>
        </Box>
        <Show when={props.isFullHeight}>
          <StyledStretchRegion ref={regionRef}>
            <Grid columns={columns} spacing={1}>
              {stretchedTasks.map((task, index) => (
                <Box key={task.id}>
                  <TaskCenterListItem
                    data-testid={`task-center-listItem-${naturalTaskCount + index}`}
                    task={task}
                    onAssigneeClick={props.onAssigneeClick}
                    onClick={() => props.onItemClick(task)}
                    hideAssignee={props.hideAssignee}
                  />
                </Box>
              ))}
            </Grid>
          </StyledStretchRegion>
        </Show>
        <Show when={props.shouldRenderShowAllButton && tasks.length > 0}>
          <Space vertical={2} />
          <Button
            title={i18n.t('entity.task.actions.showAll')}
            variant="link"
            rightIcon="navigation/chevron_right"
            onClick={props.onShowAllClick}
          />
        </Show>
      </VStack>
    </Box>
  );
}
